import { zeroPad, generateFullName } from '@/shared/lib';
import _ from 'lodash';
import { t } from 'ttag';
import { checkIcorDevice } from '@/entities/device';
import type { GenerateTitleBlockArray, Study } from '../ui/studyManagement.types';

/**
 * Decides whether a study exposes the return-status tab. Answers true for an ICOR device, false for an empty study, a Test facility or a test study, true for a follow-on study whose linked parent already carries patient-return data, and otherwise true only when the study itself has patient-return data.
 *
 * @param study - Study with its device, facility, test flag and linked studies.
 * @returns True when the return-status tab applies to the study.
 */
const checkHasReturnStatusTab = (study: Study) => {
  const isIcorDevice = checkIcorDevice(study?.device);
  if (isIcorDevice) {
    return true;
  }
  if (_.isEmpty(study)) {
    return false;
  }

  const isTestFacility = study.facility?.status === 'Test';
  if (isTestFacility) {
    return false;
  }

  if (study.isTest) {
    return false;
  }

  const isFollowOnStudy =
    study.linkedStudies?.length > 0 && study.id === study.linkedStudies?.[1]?.id;
  // *: To check study is before deploy CP-2.16.18
  const isNewStudy = !_.isEmpty(study.linkedStudies?.[0]?.patientReturn);
  const isViewPatientStatusFollowOn = isFollowOnStudy && isNewStudy;
  if (isViewPatientStatusFollowOn) {
    return true;
  }

  if (_.isEmpty(study.patientReturn)) {
    return false;
  }

  return true;
};

/**
 * Builds the title blocks shown in the study management header.
 * Lists the zero-padded study id with the BioFlux Direct flag, the patient name wired to the
 * patient info drawer, the interpreting physician name and the facility name.
 *
 * @param props - Study details plus the toggleDisplayPatientInfoDrawer handler.
 * @returns The ordered title block entries.
 */
const generateTitleBlockArray = ({
  studyDetails,
  toggleDisplayPatientInfoDrawer,
}: GenerateTitleBlockArray) => {
  const { info, facility, friendlyId, isBiofluxDirect } = studyDetails;
  const titleBlockArray = [
    {
      title: t`Study ID`,
      content: zeroPad(friendlyId),
      isBiofluxDirect,
    },
    {
      title: t`Patient`,
      content: generateFullName(info?.patient),
      handleClick: toggleDisplayPatientInfoDrawer,
    },
    {
      title: t`Physician`,
      content: generateFullName(info?.interprettingPhysician),
    },
    {
      title: t`Facility`,
      content: facility?.name || '',
    },
  ];
  return titleBlockArray;
};

export { checkHasReturnStatusTab, generateTitleBlockArray };
